<script setup lang="ts">
import BookingRulesPage from '@/components/CalendarSetup/BookingRulesPage.vue'
import ConnectionsPage from '@/components/CalendarSetup/ConnectionsPage.vue'
import ExtrasPage from '@/components/CalendarSetup/ExtrasPage.vue'
import FormsPage from '@/components/CalendarSetup/FormsPage.vue'
import MeetingDetailsPage from '@/components/CalendarSetup/MeetingDetailsPage.vue'
import MeetingLocationPage from '@/components/CalendarSetup/MeetingLocationPage.vue'
import NewAvailabilityPageWithSchedule from '@/components/CalendarSetup/NewAvailabilityPageV2.vue'
import NotificationsPageV2 from '@/components/CalendarSetup/NotificationsPageV2.vue'
import PaymentsPage from '@/components/CalendarSetup/PaymentsPage.vue'
import { useI18n } from 'vue-i18n'

import EventCalendarAlert from '@/components/CalendarSetup/EventCalendarAlert.vue'
import CalendarShareModal from '@/components/Settings/CalendarShareModal.vue'
import calendarScheduleState from '@/states/calendarScheduleState'
import { ICalendarShare, SHARE_SOURCE } from '@/types'
import {
  ArrowNarrowLeftIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Lightbulb05Icon,
  Share06Icon,
  Tool02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UIButton, UIMenu, UITooltip } from '@gohighlevel/ghl-ui'
import _, { isEqual } from 'lodash'
import { computed, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { CalendarResourceService } from '../class/services/CalendarResourceService'
import { CalendarService } from '../class/services/CalendarService'
import RoomsAndEquipmentsPage from '../components/CalendarSetup/RoomsAndEquipmentsPage.vue'
import { useNotify } from '../components/composition/notification'
import Calendar, {
  CalendarType,
  DEFAULT_CALENDAR_VALUES,
} from '../models/calendar'
import { CALENDAR_RESOURCE_TYPES } from '../models/calendar_resource'
import AppState, {
  getCalendarsInLocation,
  getCustomValues,
  getEditingCalendar,
  getEquipmentsInLocation,
  getMerchantInLocation,
  getRoomsInLocation,
  isEquipmentEnabled,
  isRoomEnabled,
  setIsAdvancedCalendarPageLoading,
  updateCalendarStore,
} from '../states/app'
import { removeCalendarIdFromQueryParams, toggleAdvancedView } from '../utils/commonModalFunctions'
import { isPaymentAllowed } from '../utils/generalFunctions'
import { getQuickTip } from '../utils/quickTips'
import { scrollToValidationError } from '../utils/validationHelper'

// Enum for calendar setup step keys
enum CalendarSetupStep {
  MEETING_DETAILS = 'meeting-details',
  STAFF_LOCATION = 'staff-location',
  AVAILABILITY = 'availability',
  BOOKING_RULES = 'booking-rules',
  FORMS = 'forms',
  PAYMENTS = 'payments',
  NOTIFICATIONS = 'notifications',
  CUSTOMIZATIONS = 'customizations',
  CONNECTIONS = 'connections',
  EQUIPMENTS = 'equipments',
}

const toast = useToast()
const { sendNotification } = useNotify()

const meetingDetailsPage = ref()
const meetingLocationPage = ref()
const availabilityPage = ref()
const bookingRulesPage = ref()
const formPage = ref()
const paymentsPage = ref()
const notificationsPage = ref()
const extrasPage = ref()
const connectionsPage = ref()
const roomsPage = ref()
const showCalendarResourcesTab = ref(false)
const route = useRoute()
const showShareCalendarModal = ref(false)
const selectedMenu = ref<CalendarSetupStep>(CalendarSetupStep.MEETING_DETAILS)
const isAdvancedSettingsExpanded = ref(false)

const { t } = useI18n()

const state = reactive({
  isLoading: false,
  isSaving: false,
  isShareSaveLoading: false,
  currentStep: 0,
  currentQuickTip: '',
  formData: {
    name: '',
    officeHours: [],
    eventColor: '#D50000',
    teamMembers: [],
    group: '',
    appoinmentPerSlot: '1',
    description: '',
    slotDuration: '30',
    slug: '',
    fileList: [],
    slotDurationUnit: 'mins',
    stripe: {
      amount: '0',
      currency: 'USD',
      chargeDescription: '',
    },
  },
  showConnectionSyncModal: false,
})

const editingCalendar = computed(() => {
  return AppState.editingCalendar as Calendar
})

const methods = {
  async loadData() {
    setIsAdvancedCalendarPageLoading(true)

    // Check if we're creating from draft data (no calendar ID yet)
    if (
      AppState.isNewCalendar &&
      !AppState.selectedCalendarId &&
      AppState.draftCalendarData
    ) {
      // Load necessary data but don't load an existing calendar
      await Promise.all([
        getMerchantInLocation(),
        getCustomValues(),
        isPaymentAllowed(AppState.locationId),
      ])

      // Create a complete calendar object from draft data with all required defaults
      AppState.editingCalendar = new Calendar({
        ...AppState.draftCalendarData,
        // Add missing required properties with defaults
        id: null, // No ID yet, will be assigned on save
        isActive: true,
        isLivePaymentMode:
          AppState.draftCalendarData.shouldAcceptPayment || false,
        enableRecurring: false,
        availabilityType: 0,
        enableConsentCheck: true,
        consentLabel: DEFAULT_CALENDAR_VALUES.CONSENT_LABEL,
        eventColor: '#039BE5',
        eventTitle: AppState.draftCalendarData.eventTitle || '{{contact.name}}', // Default event title
        formSubmitType: 'ThankYouMessage',
        formSubmitRedirectURL: '',
        formSubmitThanksMessage: DEFAULT_CALENDAR_VALUES.THANK_YOU_MESSAGE,
        autoConfirm: true, // Enable auto-confirm by default
        shouldSendAlertEmailsToAssignedMember: false,
        shouldSkipAssigningContactOwner: false,
        appoinmentTitle: '',
        notes: DEFAULT_CALENDAR_VALUES.MEETING_NOTES,
        preBufferUnit: 'mins',
        slotBufferUnit: 'mins',
        preBuffer: 0,
        slotBuffer: 0,
        enableDates: [],
        deletedDates: [],
        stickyContact: false,
        customFieldsForContactOnWidget: [],
        guestType: '',
        pixelId: '',
        allowReschedule: true,
        allowCancellation: true,
        shouldAssignContactToTeamMember: true,
        shouldSkipContactCreate: false,
        notifications: [],
        availabilityTimeConstraintType: 'range_dates',
        availabilityStartDate: null,
        availabilityEndDate: null,
        linkedCalendars: {},
        stripe: AppState.draftCalendarData.stripe || {},
        googleInvitationEmails: true,
        locationConfigurations:
          AppState.draftCalendarData.calendarType === CalendarType.EVENT
            ? [
                {
                  kind: 'custom',
                  location: AppState.draftCalendarData.meetingLocation || '',
                  position: 0,
                },
              ]
            : [],
      })
    } else {
      // Normal flow: loading existing calendar
      const calendarId = AppState.selectedCalendarId as string
      await Promise.all([
        getMerchantInLocation(),
        getCustomValues(),
        getEditingCalendar(calendarId),
        isPaymentAllowed(AppState.locationId),
      ])
    }

    setIsAdvancedCalendarPageLoading(false)
  },
  async handleCancel() {
    // Clear draft data (no need to delete from database - it was never created)
    AppState.draftCalendarData = null
    AppState.editingCalendar = null
    removeCalendarIdFromQueryParams()
    toggleAdvancedView()
    AppState.selectedCalendarId = ''
    AppState.isNewCalendar = false
    await getCalendarsInLocation(true)
    calendarScheduleState.clearState()
  },
  async isCalendarDetailsChanged(payload) {
    //Keys on which we need to check if they are changed and need to update the calendars store
    const keysToCheck = [
      'name',
      'slotDuration',
      'slotDurationUnit',
      'enableRecurring',
      'teamMembers',
      'groupId',
    ]

    return keysToCheck.some(key => {
      if (key === 'teamMembers') {
        return !isEqual(editingCalendar.value.teamMembers, payload.teamMembers)
      }
      return editingCalendar.value[key] !== payload[key]
    })
  },

  async handleSave() {
    state.isSaving = true
    state.isShareSaveLoading = true
    AppState.isConnectionSyncLoading = true
    const meetingDetailsPageData =
      await meetingDetailsPage.value.methods.emitSave()

    const meetingLocationPageData = await meetingLocationPage.value.methods.emitSave()

    const availabilityPageData = await availabilityPage.value.methods.emitSave()

    const bookingRulesPageData = await bookingRulesPage.value.methods.emitSave()

    const formPageData = await formPage.value.methods.emitSave()

    const paymentsPageData = await paymentsPage.value.methods.emitSave()

    const notificationsPageData =
      await notificationsPage.value.methods.emitSave()

    const extrasPageData = await extrasPage.value.methods.emitSave()

    const connectionsPageData = await connectionsPage.value.methods.emitSave()

    const roomsPageData = showCalendarResourcesTab.value
      ? await roomsPage.value.methods.emitSave()
      : null

    const validations = [
      { isInvalid: !meetingDetailsPageData, step: CalendarSetupStep.MEETING_DETAILS },
      { isInvalid: !meetingLocationPageData, step: CalendarSetupStep.STAFF_LOCATION },
      { isInvalid: !availabilityPageData, step: CalendarSetupStep.AVAILABILITY },
      { isInvalid: !bookingRulesPageData, step: CalendarSetupStep.BOOKING_RULES },
      { isInvalid: !formPageData, step: CalendarSetupStep.FORMS },
      { isInvalid: !paymentsPageData, step: CalendarSetupStep.PAYMENTS },
      { isInvalid: !notificationsPageData, step: CalendarSetupStep.NOTIFICATIONS },
      { isInvalid: !extrasPageData, step: CalendarSetupStep.CUSTOMIZATIONS },
      { isInvalid: !connectionsPageData, step: CalendarSetupStep.CONNECTIONS },
      {
        isInvalid: !roomsPageData && showCalendarResourcesTab.value,
        step: CalendarSetupStep.EQUIPMENTS,
      },
    ]

    // Check current tab first
    const currentTabValidation = validations.find(v => v.step === selectedMenu.value)
    if (currentTabValidation?.isInvalid) {
      state.isSaving = false
      state.isShareSaveLoading = false
      scrollToValidationError()
      return
    }

    // Then check others in order
    const invalidStep = validations.find(v => v.isInvalid)
    if (invalidStep) {
      selectedMenu.value = invalidStep.step
      state.isSaving = false
      state.isShareSaveLoading = false
      scrollToValidationError()
      return
    }
    const payload = {
      ...meetingDetailsPageData,
      ...meetingLocationPageData,
      ...availabilityPageData,
      ...bookingRulesPageData,
      ...formPageData,
      ...paymentsPageData,
      ...notificationsPageData,
      ...extrasPageData,
      ...connectionsPageData,
      calendarType: editingCalendar.value.calendarType, // Ensure calendarType is always included
    }
    const updateCalendars = await methods.isCalendarDetailsChanged(payload)

    if (formPageData.widgetConfig?.pageOrder) {
      payload.widgetConfig.pageOrder = formPageData.widgetConfig?.pageOrder
    }
    if (roomsPageData && showCalendarResourcesTab.value) {
      await methods.updateRoomsAndEquipments(roomsPageData)
    }

    if (editingCalendar.value.calendarType !== CalendarType.EVENT) {
      if (payload.linkedCalendars?.google?.id) {
        CalendarService.unlinkGoogleCalendar(editingCalendar.value.id as string)
      }
      payload.linkedCalendars = {}
    }

    if (editingCalendar.value?.isServiceCalendar) {
      payload.appoinmentPerSlot = meetingDetailsPageData.teamMembers?.length
    }
    if (
      connectionsPageData?.linkedCalendars?.google?.id &&
      editingCalendar.value.linkedCalendars?.google?.id &&
      editingCalendar.value.linkedCalendars?.google?.id !==
        connectionsPageData?.linkedCalendars?.google?.id
    ) {
      await CalendarService.unlinkGoogleCalendar(
        editingCalendar.value.id as string
      )
    }

    await methods.updateCalendar(payload, selectedMenu.value, updateCalendars)
    if (
      !connectionsPageData?.linkedCalendars?.google?.id &&
      editingCalendar.value.linkedCalendars?.google?.id
    ) {
      await CalendarService.unlinkGoogleCalendar(
        editingCalendar.value.id as string
      )
    } else if (
      connectionsPageData?.linkedCalendars?.google?.id &&
      editingCalendar.value.linkedCalendars?.google?.id !==
        connectionsPageData?.linkedCalendars?.google?.id
    ) {
      CalendarService.linkGoogleCalendar(editingCalendar.value.id as string)
    }
  },
  async updateCalendar(data, currentStepKey, updateCalendars = false) {
    try {
      // remove 'availabilityType' as its no longer needed delete deletex
      delete data.availabilityType

      if (_.isEmpty(data.openHours)) {
        delete data.openHours
      }

      // Check if this is a new calendar from draft data
      if (
        AppState.isNewCalendar &&
        !AppState.selectedCalendarId &&
        AppState.draftCalendarData
      ) {
        // CREATE the calendar for the first time
        // Ensure locationId is included (required for creation)
        const createPayload = {
          ...data,
          locationId: AppState.locationId,
        }

        const { data: createdCalendar } =
          await CalendarService.create(createPayload)

        // Update state with the newly created calendar ID
        AppState.selectedCalendarId = createdCalendar.calendar.id
        AppState.editingCalendar = createdCalendar.calendar

        await methods.saveRespectiveSchedule(data)

        // Clear draft data and mark as no longer new
        AppState.draftCalendarData = null
        AppState.isNewCalendar = false
        state.isShareSaveLoading = false
      } else {
        // UPDATE existing calendar
        await CalendarService.update(editingCalendar.value.id as string, data)
        await methods.saveRespectiveSchedule(data)
        state.isShareSaveLoading = false
        // Mark calendar as saved (no longer a draft)
        if (AppState.isNewCalendar) {
          AppState.isNewCalendar = false
        }
      }

      sendNotification({
        type: 'success',
        title: t('calendar_advanced.general.saveCalendarSuccess'),
        icon: 'CheckCircleIcon',
      })

      if (selectedMenu.value === currentStepKey) {
        debouncedCalendarRefresh()
      }

      if (updateCalendars) {
        updateCalendarStore()
      }
    } catch (error: any) {
      const message = error?.response?.data?.message
      toast.error(
        Array.isArray(message) ? message[0] : message || 'Something went wrong'
      )
      methods.updateShowCalendarSyncModal(false)
      AppState.isConnectionSyncLoading = false
      state.isSaving = false
      state.isShareSaveLoading = false
    }
  },
  async saveRespectiveSchedule(calendar: any) {
    try {
      if (
        AppState.isScheduleEnabled &&
        editingCalendar.value?.calendarType === CalendarType.EVENT
      ) {
        const schedule = await calendarScheduleState.getEventCalendarSchedules(
          editingCalendar.value?.id
        )
        schedule.openHoursAndAvailabilitiesToRules(
          calendar?.openHours,
          calendar?.availabilities
        )
        const selectedTimezone =
          availabilityPage.value?.getSelectedEventTimezone?.() ||
          schedule.timezone ||
          AppState.locationTimezone
        if (!schedule.id) {
          // Create schedule from the open hours for new calendars
          const { rules, name } = schedule.toJSON()
          await calendarScheduleState.createSchedule({
            rules,
            timezone: selectedTimezone,
            calendarIds: [editingCalendar.value?.id],
            isCustom: true,
            locationId: AppState.locationId,
            name: name || editingCalendar.value?.name,
            userId: undefined, // EVENT calendars don't have a specific user
          })
          return
        }
        await CalendarService.updateCalendarSchedule(schedule.id, {
          rules: schedule.rules,
          timezone: selectedTimezone,
          calendarIds: schedule.calendarIds,
          isCustom: true,
        })
      }
    } catch (error) {
      console.error('Error saving respective schedule', error)
    }
  },
  getNextStepKey(currentKey: CalendarSetupStep): CalendarSetupStep | null {
    const currentIndex = steps.value.findIndex(step => step.key === currentKey)
    if (currentIndex === -1 || currentIndex === steps.value.length - 1) {
      return null
    }
    return steps.value[currentIndex + 1]?.key || null
  },

  async refreshCalendar() {
    await getEditingCalendar(editingCalendar.value.id)

    // Connections is always last (Equipments is inserted before it when shown).
    const lastStepKey =
      steps.value[steps.value.length - 1]?.key ?? CalendarSetupStep.CONNECTIONS
    const isLastStep = selectedMenu.value === lastStepKey

    if (isLastStep) {
      AppState.editingCalendar = null

      toggleAdvancedView()
      AppState.selectedCalendarId = ''
      AppState.isNewCalendar = false
      await getCalendarsInLocation(true)
    }

    state.isSaving = false

    if (
      !state.showConnectionSyncModal &&
      !AppState.isUserAvailabilityUpdated
    ) {
      const nextKey = methods.getNextStepKey(selectedMenu.value)
      if (nextKey) {
        selectedMenu.value = nextKey
      }
    }
    methods.updateShowCalendarSyncModal(false)
    AppState.isConnectionSyncLoading = false

    //reset the isUserAvailabilityUpdated flag
    AppState.isUserAvailabilityUpdated = false
  },
  async updateRoomsAndEquipments(roomsAndEquipments) {
    const calendarId = editingCalendar.value.id
    const {
      selectedEquipments,
      selectedRooms,
      toRemoveEquipments,
      toRemoveRooms,
    } = roomsAndEquipments
    const allRooms: any[] = AppState.globalRooms
    const allEquipments: any[] = AppState.globalEquipments
    const roomsToUpdate: any[] = [],
      equipmentsToUpdate: any[] = []
    for (const room of allRooms) {
      if (
        !room?.calendarIds?.includes(calendarId) &&
        selectedRooms?.includes(room._id)
      ) {
        roomsToUpdate.push(room._id)
      }
    }
    const promises: any[] = []
    for (const toUpdateRoom of roomsToUpdate) {
      promises.push(
        CalendarResourceService.update(
          toUpdateRoom,
          { calendarIds: [calendarId] },
          CALENDAR_RESOURCE_TYPES.ROOMS
        )
      )
    }
    for (const removeRoom of toRemoveRooms) {
      if (
        allRooms
          ?.find((e: any) => e._id === removeRoom)
          ?.calendarIds?.includes(calendarId)
      ) {
        promises.push(
          CalendarResourceService.update(
            removeRoom,
            { toDeleteCalendarIds: [calendarId] },
            CALENDAR_RESOURCE_TYPES.ROOMS
          )
        )
      }
    }
    for (const equipment of allEquipments) {
      if (
        !equipment?.calendarIds?.includes(calendarId) &&
        selectedEquipments?.includes(equipment._id)
      ) {
        equipmentsToUpdate.push(equipment._id)
      }
    }
    for (const toUpdateEquipment of equipmentsToUpdate) {
      promises.push(
        CalendarResourceService.update(
          toUpdateEquipment,
          { calendarIds: [calendarId] },
          CALENDAR_RESOURCE_TYPES.EQUIPMENTS
        )
      )
    }
    for (const removeEquipment of toRemoveEquipments) {
      promises.push(
        CalendarResourceService.update(
          removeEquipment,
          { toDeleteCalendarIds: [calendarId] },
          CALENDAR_RESOURCE_TYPES.EQUIPMENTS
        )
      )
    }
    await Promise.all(promises)
    await Promise.all([getRoomsInLocation(true), getEquipmentsInLocation(true)])
  },
  updateShowCalendarSyncModal(showModal: boolean) {
    state.showConnectionSyncModal = showModal
  },
}

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const steps = computed(() => {
  // Use calendarType directly for more reliable checking, with fallback to draft data
  const calendarType = editingCalendar.value?.calendarType || AppState.draftCalendarData?.calendarType
  const isEventCalendar = calendarType === CalendarType.EVENT
  const data = [
    {
      label: isServiceCalendar.value
        ? t('calendar_advanced.general.service_details')
        : t('calendar_advanced.general.meeting_details'),
      key: CalendarSetupStep.MEETING_DETAILS,
    },
    {
      label: isEventCalendar ? t('calendar_advanced.general.meeting_location') : t('calendar_advanced.general.staff_location'),
      key: CalendarSetupStep.STAFF_LOCATION,
    },
    {
      label: t('calendar_advanced.general.availability'),
      key: CalendarSetupStep.AVAILABILITY,
    },
    {
      label: t('calendar_advanced.general.booking_rules'),
      key: CalendarSetupStep.BOOKING_RULES,
    },
    {
      label: t('calendar_advanced.general.form_and_confirmation'),
      key: CalendarSetupStep.FORMS,
    },
    {
      label: t('calendar_advanced.general.payments'),
      key: CalendarSetupStep.PAYMENTS,
    },
    {
      label: t('calendar_advanced.general.notifications_and_policies'),
      key: CalendarSetupStep.NOTIFICATIONS,
    },
    {
      label: t('calendar_advanced.general.widget_appearance'),
      key: CalendarSetupStep.CUSTOMIZATIONS,
    },
  ]
  data.push({
    label: isEventCalendar
      ? t('calendar_advanced.general.external_sync_and_channel')
      : t('calendar_advanced.general.booking_channels'),
    key: CalendarSetupStep.CONNECTIONS,
  })
  return data
})

const stepsAlwaysVisible = computed(() => {
  const all = steps.value
  const bookingRulesIndex = all.findIndex(s => s.key === CalendarSetupStep.BOOKING_RULES)
  if (bookingRulesIndex === -1) return all
  return all.slice(0, bookingRulesIndex + 1)
})

const stepsInAdvanced = computed(() => {
  const all = steps.value
  const bookingRulesIndex = all.findIndex(s => s.key === CalendarSetupStep.BOOKING_RULES)
  if (bookingRulesIndex === -1) return []
  return all.slice(bookingRulesIndex + 1)
})

// Keep advanced settings expanded when user is on any step under "Advanced settings"
watch(
  () => selectedMenu.value,
  (step) => {
    const isAdvancedStep = stepsInAdvanced.value.some(s => s.key === step)
    if (isAdvancedStep) {
      isAdvancedSettingsExpanded.value = true
    }
  },
  { immediate: true }
)

const currentStepId = computed(() => steps.value[state.currentStep]?.id)

// Memoized quick tip that only changes when selectedMenu or calendarType changes
const currentQuickTip = computed(() => {
  const menuKey = selectedMenu.value
  const calendarType = editingCalendar.value?.calendarType || AppState.draftCalendarData?.calendarType
  return getQuickTip(menuKey, calendarType, t)
})

const selectedCalendarData = computed(() => {
  const data: ICalendarShare = {
    id: editingCalendar.value?.id,
    slug: editingCalendar.value?.widgetSlug,
    name: editingCalendar.value?.name,
    calendarType: editingCalendar.value?.calendarType,
    providerId: editingCalendar.value?.providerId,
    slotDuration: editingCalendar.value?.slotDuration.toString(),
    slotDurationUnit: editingCalendar.value?.slotDurationUnit,
    widgetType: editingCalendar.value?.widgetType,
    source: SHARE_SOURCE.CALENDAR,
    schedulePath: 'bookings',
    permanentPath: 'booking',
    otlPath: 'otl',
    showOneTimeLink: true,
    isSaving: showMessage.value,
    loadingMessage: showMessage.value
      ? t('calendar_advanced.general.saving_changes')
      : '',
  }
  return data
})

watch(
  () => editingCalendar.value,
  async newValue => {
    if (
      (isRoomEnabled() || isEquipmentEnabled()) &&
      newValue?.isServiceCalendar
    ) {
      const equipmentsStep = {
        label: t('calendar_advanced.general.equipments'),
        key: CalendarSetupStep.EQUIPMENTS,
      }
      // Insert equipments step before connections if connections exists, otherwise at the end
      const connectionsIndex = steps.value.findIndex(
        s => s.key === CalendarSetupStep.CONNECTIONS
      )
      if (connectionsIndex >= 0) {
        steps.value.splice(connectionsIndex, 0, equipmentsStep)
      } else {
        steps.value.push(equipmentsStep)
      }
      await Promise.all([getRoomsInLocation(), getEquipmentsInLocation()])
      showCalendarResourcesTab.value = true
    }
  }
)

// Scroll to top when tab changes
watch(
  () => selectedMenu.value,
  () => {
    const container = document.getElementById(
      'calendar-advanced-setup-container'
    )
    if (container) {
      container.scrollTo({ top: 0, behavior: 'auto' })
    }
  }
)

watch(
  () => AppState.selectedCalendarSetupStep,
  (newStep) => {
    if (newStep && newStep !== selectedMenu.value) {
      selectedMenu.value = newStep as CalendarSetupStep
      AppState.selectedCalendarSetupStep = newStep
    }
  },
  { immediate: true }
)

const handleShareCalendar = async () => {
  showShareCalendarModal.value = true
  await methods.handleSave()
}

const handleTroubleshoot = async () => {
  await methods.handleSave()
  AppState.parentApp?.then(parent => {
    parent.emit('open-troubleshoot-calendar', {
      calendarId: editingCalendar.value?.id,
    })
  })
}

const showMessage = computed(() => {
  return state.isShareSaveLoading
})

methods.loadData()
const debouncedCalendarRefresh = _.debounce(methods.refreshCalendar, 2000)
</script>

<template>
  <div class="flex w-full flex-col justify-center bg-[#F9FAFB]">
    <div
      class="mb-5 flex w-full items-center justify-between rounded-sm border-[0.8px] border-gray-300 bg-white p-[12px] text-sm"
    >
      <UIButton
        id="create-calendar-close"
        type="default"
        class="p-0"
        :quaternary="true"
        size="small"
        @click="methods.handleCancel"
      >
        <div class="flex flex-row items-center gap-2">
          <ArrowNarrowLeftIcon class="h-5 w-5 text-gray-500" />
          <div class="font-normal text-gray-500">
            {{ $t('settings.calendar.back_message') }}
          </div>
        </div>
      </UIButton>
      <div class="flex flex-col items-start gap-[5px] pl-3 text-lg">
        {{
          AppState.isNewCalendar
            ? $t('calendar_advanced.general.create')
            : $t('calendar_advanced.general.edit')
        }}
        <template v-if="editingCalendar?.name">
          -
          {{ editingCalendar.name }}
        </template>
      </div>

      <div class="flex space-x-3">
        <UITooltip trigger="hover">
          <template #trigger>
            <UIButton
              :id="`share-calendar-${editingCalendar?.id}`"
              type="default"
              size="small"
              @click="handleShareCalendar"
            >
              <Share06Icon class="h-5 w-5 text-gray-700" />
            </UIButton>
          </template>
          <div>
            {{ $t('settings.calendar.share_calendar') }}
          </div>
        </UITooltip>
        <UITooltip trigger="hover">
          <template #trigger>
            <UIButton
              :id="`troubleshoot-calendar-${editingCalendar?.id}`"
              type="default"
              size="small"
              @click="handleTroubleshoot"
            >
              <Tool02Icon class="h-5 w-5 text-gray-700" />
            </UIButton>
          </template>
          <div>
            {{ $t('settings.calendar.troubleshoot_calendar_link') }}
          </div>
        </UITooltip>
        <UIButton
          id="create-calendar-save"
          type="primary"
          :loading="state.isSaving"
          :disabled="AppState.errorInDurationPickerMap.allowBookingAfter"
          @click="methods.handleSave"
          size="small"
        >
          {{ $t('calendar_advanced.general.save') }}
        </UIButton>
      </div>
    </div>

    <div class="mt-[24px] flex w-full flex-col items-center">
      <div class="flex w-[95%] justify-center gap-4">
        <div class="flex w-[280px] flex-col pr-[20px]">
          <UIMenu v-model:value="selectedMenu" :options="stepsAlwaysVisible" />

          <button
            type="button"
            class="advanced-settings-toggle flex w-full items-center gap-2 rounded-md text-left text-sm font-medium text-gray-700 "
            @click="isAdvancedSettingsExpanded = !isAdvancedSettingsExpanded"
          >
            <span class="advanced-settings-toggle__icon flex shrink-0 items-center justify-center text-gray-500">
              <component
                :is="isAdvancedSettingsExpanded ? ChevronDownIcon : ChevronRightIcon"
                class="h-5 w-5"
              />
            </span>
            <span class="advanced-settings-toggle__label">{{ $t('calendar_advanced.general.advanced_settings') }}</span>
          </button>
          
          <div v-show="isAdvancedSettingsExpanded">
            <UIMenu v-model:value="selectedMenu" :options="stepsInAdvanced" />
          </div>

          <div class="flex flex-col space-y-2 pt-4 pl-8">
            <div class="flex flex-row items-center gap-2">
              <Lightbulb05Icon class="h-5 w-5 text-gray-500" />
              <div class="font-medium text-gray-500">
                {{ $t('calendar_advanced.general.quick_tip') }}
              </div>
            </div>
            <div
              class="mt-[10px] text-[12px] leading-5 text-gray-400"
              v-html="currentQuickTip"
            ></div>
          </div>
        </div>

        <div class="mr-[20px] flex w-full flex-grow flex-col">
          <div
            class="mb-[10px]"
            v-if="editingCalendar?.calendarType === CalendarType.EVENT"
          >
            <EventCalendarAlert :icon-position="'end'" />
          </div>

          <div
            class="h-fit overflow-y-auto rounded-xl border-b border-gray-200"
            id="calendar-advanced-setup-container"
            :class="{
              'pointer-events-none blur-md filter':
                AppState.isAdvancedCalendarPageLoading,
              'max-h-[calc(100vh-12rem)]': editingCalendar?.isEventCalendar,
              'max-h-[calc(100vh-9rem)]': !editingCalendar?.isEventCalendar,
            }"
          >
            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.MEETING_DETAILS,
              }"
            >
              <MeetingDetailsPage
                ref="meetingDetailsPage"
                @on-save="
                  data =>
                    methods.updateCalendar(
                      data,
                      CalendarSetupStep.MEETING_DETAILS
                    )
                "
              />
            </div>

            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.STAFF_LOCATION,
              }"
            >
              <MeetingLocationPage ref="meetingLocationPage" />
            </div>

            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.AVAILABILITY,
              }"
            >
              <NewAvailabilityPageWithSchedule
                ref="availabilityPage"
                @on-save="
                  data =>
                    methods.updateCalendar(data, CalendarSetupStep.AVAILABILITY)
                "
                @save-calendar="methods.handleSave()"
              />
            </div>

            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.BOOKING_RULES,
              }"
            >
              <BookingRulesPage
                ref="bookingRulesPage"
                @on-save="
                  data =>
                    methods.updateCalendar(
                      data,
                      CalendarSetupStep.BOOKING_RULES
                    )
                "
              />
            </div>

            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.FORMS,
              }"
            >
              <FormsPage
                ref="formPage"
                @on-save="
                  data => methods.updateCalendar(data, CalendarSetupStep.FORMS)
                "
              />
            </div>

            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.PAYMENTS,
              }"
            >
              <PaymentsPage
                ref="paymentsPage"
                @on-save="
                  data =>
                    methods.updateCalendar(data, CalendarSetupStep.PAYMENTS)
                "
              />
            </div>

            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.NOTIFICATIONS,
              }"
            >
              <NotificationsPageV2
                ref="notificationsPage"
                @on-save="
                  data =>
                    methods.updateCalendar(
                      data,
                      CalendarSetupStep.NOTIFICATIONS
                    )
                "
              />
            </div>

            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.CUSTOMIZATIONS,
              }"
            >
              <ExtrasPage
                ref="extrasPage"
                @on-save="
                  data =>
                    methods.updateCalendar(
                      data,
                      CalendarSetupStep.CUSTOMIZATIONS
                    )
                "
              />
            </div>

            <div
              :class="{
                hidden: selectedMenu !== CalendarSetupStep.CONNECTIONS,
              }"
            >
              <ConnectionsPage
                ref="connectionsPage"
                :showConnectionSyncModal="state.showConnectionSyncModal"
                :update-show-connection-sync-modal="
                  methods.updateShowCalendarSyncModal
                "
                @on-save="methods.handleSave"
                @on-cancel="methods.handleCancel"
              />
            </div>

            <div
              v-if="showCalendarResourcesTab"
              :class="{ hidden: selectedMenu !== CalendarSetupStep.EQUIPMENTS }"
            >
              <RoomsAndEquipmentsPage
                ref="roomsPage"
                @on-save="
                  data =>
                    methods.updateCalendar(data, CalendarSetupStep.EQUIPMENTS)
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <CalendarShareModal
    v-if="showShareCalendarModal"
    :show="showShareCalendarModal"
    :data="selectedCalendarData"
    @close-modal="showShareCalendarModal = false"
  />
</template>

<style lang="scss">
.n-menu .n-menu-item {
  margin: 3px 0px;
  height: fit-content;
}

.n-menu-item-content.n-menu-item-content--selected .n-menu-item-content-header {
  color: var(--primary-700) !important;
}

.n-menu .n-menu-item-content .n-menu-item-content-header {
  color: var(--gray-500);
  padding: 10px 0px;
  white-space: break-spaces;
  line-height: 1.5;
}

.advanced-settings-toggle {
  padding: 0 0 5px 0;
  margin: 3px 0;
  align-items: center;
  line-height: 1.5;
}

.advanced-settings-toggle__icon {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.advanced-settings-toggle__label {
  line-height: 1.5;
  display: inline-block;
  vertical-align: middle;
  color: var(--gray-500);
  font-weight: 400;
}

#calendar-advanced-setup-container {
  .n-input-number .n-input-wrapper {
    height: 40px;
    align-items: center;
  }
}
</style>
